<script setup>
// Potansiyel Musteriler sekmesi: once hesabin form listesi, form secilince o
// formun kayitlari.
//
// Kayitlar form secilmeden yuklenmiyor; bir hesapta onlarca form olabiliyor
// ve hepsinin kaydini birden cekmek hem yavas hem de kisisel veriyi gereksiz
// yere ekrana dokmek olurdu.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import { aracCalistir, diziyeCevir, sayiBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: null },
});

const { t } = useI18n();
const route = useRoute();

const formlar = ref([]);
const formlarYukleniyor = ref(false);
const hata = ref('');

const seciliForm = ref(null);
const kayitlar = ref([]);
const kayitYukleniyor = ref(false);
const kayitHata = ref('');

const reklamKimligi = ref('');
const reklamKayitlari = ref([]);
const reklamYukleniyor = ref(false);
const reklamHata = ref('');

const accountId = computed(() => route.params.accountId);

// Form durumu ham Ingilizce sabit olarak geliyor (ACTIVE, ARCHIVED...).
// Bilinen degerler cevriliyor; taninmayan deger oldugu gibi kaliyor ki durum
// bilgisi kaybolmasin.
const DURUM_KARSILIKLARI = {
  ACTIVE: 'AKTIF',
  PAUSED: 'DURAKLATILDI',
  ARCHIVED: 'ARSIVLENDI',
  DRAFT: 'TASLAK',
  DELETED: 'SILINDI',
};

const durumMetni = deger => {
  const ham = String(deger || '').toUpperCase();
  if (!ham) return '—';
  const ek = DURUM_KARSILIKLARI[ham];
  return ek ? t(`META_ADS.ISTATISTIKLER.POTANSIYEL.DURUM.${ek}`) : ham;
};

const formSutunlari = computed(() => [
  {
    anahtar: 'name',
    baslik: t('META_ADS.ISTATISTIKLER.POTANSIYEL.SUTUN.FORM_ADI'),
  },
  { anahtar: 'id', baslik: t('META_ADS.ISTATISTIKLER.POTANSIYEL.SUTUN.KIMLIK') },
  {
    anahtar: 'status',
    baslik: t('META_ADS.ISTATISTIKLER.POTANSIYEL.SUTUN.DURUM'),
    bicim: durumMetni,
  },
  {
    anahtar: 'leads_count',
    baslik: t('META_ADS.ISTATISTIKLER.POTANSIYEL.SUTUN.KAYIT_SAYISI'),
    bicim: deger => (deger === null || deger === undefined ? '—' : sayiBicimle(deger)),
  },
]);

// Meta kayit alanlarini teknik adiyla dondurur (full_name, phone_number).
// Standart alanlarin okunur karsiligi burada; listede olmayan alan adi oldugu
// gibi gosterilir, cunku form sahibi kendi alanini serbest adlandirabiliyor ve
// uydurulmus bir etiket yanlis bilgi verir.
const KAYIT_ALAN_ETIKETLERI = {
  full_name: 'AD_SOYAD',
  first_name: 'AD',
  last_name: 'SOYAD',
  email: 'EPOSTA',
  work_email: 'IS_EPOSTASI',
  phone_number: 'TELEFON',
  work_phone_number: 'IS_TELEFONU',
  company_name: 'SIRKET',
  job_title: 'UNVAN',
  street_address: 'ADRES',
  city: 'SEHIR',
  state: 'BOLGE',
  country: 'ULKE',
  zip: 'POSTA_KODU',
  date_of_birth: 'DOGUM_TARIHI',
  gender: 'CINSIYET',
};

const alanEtiketi = ad => {
  const ek = KAYIT_ALAN_ETIKETLERI[String(ad || '').toLowerCase()];
  return ek ? t(`META_ADS.ISTATISTIKLER.POTANSIYEL.ALAN.${ek}`) : String(ad || '');
};

// Sabit sutun acmak yerine tek hucrede "Etiket: deger" olarak yaziyoruz;
// degeri bos olan alan hic basilmiyor ki hucre bos etiketlerle dolmasin.
const alanlariMetne = alanlar => {
  if (!Array.isArray(alanlar) || !alanlar.length) return '—';
  const parcalar = alanlar
    .map(a => {
      const deger = (a?.values || []).filter(d => d !== null && d !== '').join(', ');
      if (!deger) return '';
      const etiket = alanEtiketi(a?.name);
      return etiket ? `${etiket}: ${deger}` : deger;
    })
    .filter(Boolean);
  return parcalar.length ? parcalar.join(' · ') : '—';
};

// Kayit zamani ISO damgasi olarak geliyor; listede okunur tarih/saat duruyor.
const tarihBicimle = deger => {
  const metin = String(deger ?? '');
  if (!metin) return '—';
  const tarih = new Date(metin);
  return Number.isNaN(tarih.getTime())
    ? metin
    : tarih.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

const kayitSutunlari = computed(() => [
  {
    anahtar: 'created_time',
    baslik: t('META_ADS.ISTATISTIKLER.POTANSIYEL.SUTUN.OLUSTURMA'),
    bicim: tarihBicimle,
  },
  { anahtar: 'id', baslik: t('META_ADS.ISTATISTIKLER.POTANSIYEL.SUTUN.KIMLIK') },
  {
    anahtar: 'field_data',
    baslik: t('META_ADS.ISTATISTIKLER.POTANSIYEL.SUTUN.BILGILER'),
    bicim: alanlariMetne,
  },
]);

const formlariYukle = async () => {
  if (!props.hesap) return;
  formlarYukleniyor.value = true;
  hata.value = '';
  seciliForm.value = null;
  kayitlar.value = [];
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_lead_forms', {
      account_id: props.hesap,
    });
    formlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    formlar.value = [];
  } finally {
    formlarYukleniyor.value = false;
  }
};

const kayitlariYukle = async form => {
  if (!form?.id) return;
  seciliForm.value = form;
  kayitYukleniyor.value = true;
  kayitHata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_leads', {
      form_id: form.id,
    });
    kayitlar.value = diziyeCevir(sonuc);
  } catch (e) {
    kayitHata.value = e.response?.data?.error || e.message;
    kayitlar.value = [];
  } finally {
    kayitYukleniyor.value = false;
  }
};

// Formdan degil, dogrudan tek bir reklamdan gelen kayitlar. Kampanya bazli
// inceleme icin ayri tutuldu.
const reklamKayitlariniYukle = async () => {
  if (!reklamKimligi.value) return;
  reklamYukleniyor.value = true;
  reklamHata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_ad_leads', {
      ad_id: reklamKimligi.value,
    });
    reklamKayitlari.value = diziyeCevir(sonuc);
  } catch (e) {
    reklamHata.value = e.response?.data?.error || e.message;
    reklamKayitlari.value = [];
  } finally {
    reklamYukleniyor.value = false;
  }
};

// Reklam kimligiyle cekilen kayitlar formlariYukle icinde temizlenmiyor cunku
// o fonksiyon ayni zamanda "Yenile" dugmesi; hesap degisiminde ise onceki
// hesabin kisisel verisi ekranda kalmamali, temizlik burada yapiliyor.
watch(() => props.hesap, () => {
  reklamKimligi.value = '';
  reklamKayitlari.value = [];
  reklamHata.value = '';
  formlariYukle();
});

onMounted(formlariYukle);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <p v-if="!hesap" class="text-sm text-n-slate-11">
      {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.HESAP_SEC') }}
    </p>

    <template v-else>
      <section class="flex flex-col gap-2">
        <div class="flex items-center gap-3">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.ISTATISTIKLER.POTANSIYEL.FORMLAR') }}
          </h2>
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-refresh-cw"
            :label="t('META_ADS.ISTATISTIKLER.POTANSIYEL.YENILE')"
            @click="formlariYukle"
          />
        </div>

        <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

        <AracTablosu
          :satirlar="formlar"
          :sutunlar="formSutunlari"
          :yukleniyor="formlarYukleniyor"
          :bos-metin="t('META_ADS.ISTATISTIKLER.POTANSIYEL.FORM_YOK')"
        >
          <template #eylemler="{ satir }">
            <Button
              variant="faded"
              size="sm"
              icon="i-lucide-list"
              :label="t('META_ADS.ISTATISTIKLER.POTANSIYEL.KAYITLARI_GOSTER')"
              @click="kayitlariYukle(satir)"
            />
          </template>
        </AracTablosu>
      </section>

      <section class="flex flex-col gap-2 pt-4 border-t border-n-weak">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.ISTATISTIKLER.POTANSIYEL.KAYITLAR') }}
          <span v-if="seciliForm" class="font-normal text-n-slate-11">
            · {{ seciliForm.name || seciliForm.id }}
          </span>
        </h2>

        <p v-if="kayitHata" class="text-sm text-n-ruby-11">{{ kayitHata }}</p>

        <p v-if="!seciliForm" class="text-sm text-n-slate-11">
          {{ t('META_ADS.ISTATISTIKLER.POTANSIYEL.FORM_SECIN') }}
        </p>
        <AracTablosu
          v-else
          :satirlar="kayitlar"
          :sutunlar="kayitSutunlari"
          :yukleniyor="kayitYukleniyor"
          :bos-metin="t('META_ADS.ISTATISTIKLER.POTANSIYEL.KAYIT_YOK')"
        />
      </section>

      <section class="flex flex-col gap-3 pt-4 border-t border-n-weak">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.ISTATISTIKLER.POTANSIYEL.REKLAM_KAYITLARI') }}
        </h2>
        <div class="flex flex-wrap items-center gap-3">
          <input
            v-model="reklamKimligi"
            type="text"
            class="!mb-0 !w-auto min-w-[240px]"
            :placeholder="t('META_ADS.ISTATISTIKLER.POTANSIYEL.REKLAM_KIMLIGI')"
          />
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-search"
            :label="t('META_ADS.ISTATISTIKLER.POTANSIYEL.GETIR')"
            :disabled="!reklamKimligi || reklamYukleniyor"
            @click="reklamKayitlariniYukle"
          />
        </div>

        <p v-if="reklamHata" class="text-sm text-n-ruby-11">{{ reklamHata }}</p>

        <AracTablosu
          :satirlar="reklamKayitlari"
          :sutunlar="kayitSutunlari"
          :yukleniyor="reklamYukleniyor"
          :bos-metin="t('META_ADS.ISTATISTIKLER.POTANSIYEL.KAYIT_YOK')"
        />
      </section>
    </template>
  </div>
</template>
